import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface PastelInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

const EMOJIS = ["✨", "💫", "🌸", "🎀", "💜", "🌟"];

const DOT_COLORS = ["#ffb7d5", "#c8a4ff", "#a0e7ff"];

export const PastelInsights: React.FC<PastelInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Active bullet index logic
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Bouncy spring entrance for text
  const textScale = spring({
    frame: elapsedFrames,
    fps,
    config: { stiffness: 180, damping: 12, mass: 0.6 },
  });

  const opacity = interpolate(elapsedFrames, [0, 6], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance (from first bullet)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { stiffness: 180, damping: 12, mass: 0.6 },
  });

  const activeEmoji = EMOJIS[activeIndex % EMOJIS.length];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "2px solid #ffb7d5",
          borderRadius: "24px",
          padding: "40px",
          boxShadow: "0 8px 32px rgba(200,164,255,0.2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          position: "relative",
          overflow: "hidden",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Top emoji */}
        <div
          style={{
            fontSize: "36px",
            opacity,
            transform: `scale(${textScale})`,
            lineHeight: 1,
          }}
        >
          {activeEmoji}
        </div>

        {/* Counter pill badge + brand badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div
            style={{
              background: "#ffb7d5",
              borderRadius: "50px",
              padding: "4px 14px",
              fontFamily: "'Nunito', sans-serif",
              fontSize: "12px",
              fontWeight: 800,
              color: "#2d1b3d",
              letterSpacing: "0.5px",
            }}
          >
            #{activeIndex + 1}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#c8a4ff"
                accentColor="#ffb7d5"
                size={68}
                style="glass"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        {/* Main bullet text */}
        <div
          style={{
            opacity,
            transform: `scale(${textScale})`,
            minHeight: "260px",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: "50px",
              fontWeight: 800,
              lineHeight: 1.4,
              color: "#2d1b3d",
              textAlign: "center",
              margin: 0,
              width: "100%",
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 800,
                color: "#ffb7d5",
                background: "linear-gradient(135deg, #ffb7d5, #c8a4ff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        {/* Bottom colored dots decoration */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {DOT_COLORS.map((color, i) => (
            <div
              key={i}
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
